import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import classNames from "classnames";
import * as React from "react";

import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";

const difficulties = [{
    id: "easy",
    label: "Easy",
}, {
    id: "medium",
    label: "Medium",
}, {
    id: "hard",
    label: "Hard",
}];

interface LayoutsStates {
    isLoaded: boolean;
    layouts: any[];
    inlineAnchorEl: any;
}

interface LayoutProps {
    classes: any;
}

class LayoutsRoot extends React.Component<LayoutProps, LayoutsStates> {

    constructor(props) {
        super(props);

        this.state = {
            isLoaded: false,
            layouts: [],
            inlineAnchorEl: null,
        };

        document.title = Helpers.documentTitle("Layouts");
    }

    componentDidMount() {
        this.fetchData();
    }

    refreshData = () => {
        this.setState({ isLoaded: false }, () => {
            this.fetchData();
        });
    }

    handleClick = event => {
        this.setState({ inlineAnchorEl: event.currentTarget });
    }

    handleClose = (difficulty) => {
        const layoutId = parseInt(this.state.inlineAnchorEl.getAttribute("element-id"), 10);
        const tempLayouts = [ ...this.state.layouts ];
        const layoutIndex = tempLayouts.findIndex(layout => layout.id === layoutId);

        if (layoutIndex > -1) {
            tempLayouts[layoutIndex].difficulty = difficulty;
            // Update database
            this.updateDifficulty(layoutId, difficulty);
        }

        this.setState({
            inlineAnchorEl: null,
            layouts: tempLayouts,
        });
    }

    updateDifficulty = (layoutId, difficulty) =>
        axios.put(`${Config.getInstance.apiUrl}/api/layout/${layoutId}/difficulty`, {
            difficulty,
        } , {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
        }).catch(() => {
            this.setState({
                isLoaded: true,
            });
        })

    fetchData = () =>
        axios.get(`${Config.getInstance.apiUrl}/api/layout`, {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
        }).then(response => {
            this.setState({
                layouts: response.data.data || [],
                isLoaded: true,
            });
        }).catch(() => {
            this.setState({
                isLoaded: true,
            });
        })

    formatDifficulty = (row) => {
        const activeElement = difficulties.find(difficulty => difficulty.id === row.difficulty);

        return <Button disableElevation
            aria-owns={this.state.inlineAnchorEl ? "simple-menu" : undefined}
            onClick={this.handleClick}
            aria-haspopup="true"
            element-id={row.id}
        >
            {activeElement ? activeElement.label : "Not defined"}
            <ArrowDropDown className={classNames(this.props.classes.dropdownIcon, this.props.classes.iconExtraSmall)}/>
        </Button>;
    }

    render() {
        const { classes } = this.props;

        return <div className="root">
                <Paper className="root" elevation={0}>
                    <div style={{ float: "left", padding: "15px 22px 15px 22px", width: "100%" }}>
                        <div className={classes.title}>
                            <Typography variant="h6" id="tableTitle">
                                Layouts
                            </Typography>
                        </div>
                        <div className={classes.actions}>
                            <Tooltip title="Refresh list" placement="bottom">
                                <Button disableElevation color="primary" variant="contained" onClick={this.refreshData}>
                                    <RefreshIcon className={classNames(classes.leftIcon, classes.iconSmall)} />
                                    Refresh
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    <div style={{ float: "left", width: "100%" }}>
                        <LoadingWrapper loading={!this.state.isLoaded}>
                            <Table className="min-table-size">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ width: 100 }}>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell style={{ width: 250 }}>Difficulty</TableCell>
                                        <TableCell style={{ width: 250 }}>Last update</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {this.state.layouts.map(row => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{this.formatDifficulty(row)}</TableCell>
                                            <TableCell>{Helpers.formatDateWithTime(row.updated_at)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </LoadingWrapper>
                    </div>
                </Paper>

                <Menu
                    id="simple-menu"
                    anchorEl={this.state.inlineAnchorEl}
                    open={Boolean(this.state.inlineAnchorEl)}
                    onClose={this.handleClose}
                >
                    {difficulties.map(difficulty => (
                        <MenuItem onClick={() => this.handleClose(difficulty.id)}>{difficulty.label}</MenuItem>
                    ))}
                </Menu>
        </div>;
    }
}

const styles: any = theme => ({
    leftIcon: {
        marginRight: 8,
    },
    rightIcon: {
        marginLeft: 8,
    },
    iconSmall: {
        fontSize: 20,
    },
    iconExtraSmall: {
        fontSize: 16,
    },
    dropdownIcon: {
        marginLeft: 16,
    },
    progress: {
        margin: 16,
    },
    title: {
        flex: "0 0 auto",
        float: "left",
    },
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },

});

export const Layouts = withStyles(styles)(LayoutsRoot);
